import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import { useCanMutate } from '../../../hooks/useCanMutate';
import { useModuleSettings } from '../../../contexts/ModuleSettingsContext';
import recetaPlatoService from '../../../services/recetaPlatoService';
import entryGuideService from '../../../services/entryGuideService';
import produccionGuiaService from '../../../services/produccionGuiaService';
import { productService } from '../../../services/productService';
import { getGuiaConfigBySlug } from '../../../utils/produccionGuiaPaths';
import { lineasInsumoEscaladosRecetaApi } from '../../../data/mockSalidasProduccion';
import {
  applyKardexToInsumoResumen,
  buildDesgloseInsumoPorReceta,
  buildExplosionPepAnalisis,
  buildResumenByproductsFromInsumos,
  buildResumenInsumosGuia,
  buildResumenPlatosGuia,
  formatearCantidadReceta4Dec,
  formatearCostoUnitario4Dec,
  getGuiaItemsWithQuantity,
  isSubproductoProduct,
} from '../../../utils/produccionGuiaCostos';

const fmtMoney = (n) => {
  const x = Number(n);
  const v = Number.isFinite(x) ? x : 0;
  return v.toLocaleString('es-PE', { style: 'currency', currency: 'PEN', minimumFractionDigits: 2 });
};

function normalizarHoraHHmm(raw, fallback = '08:00') {
  const t = String(raw || '').trim();
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return fallback;
  const hh = Math.min(23, Math.max(0, parseInt(m[1], 10)));
  const mm = Math.min(59, Math.max(0, parseInt(m[2], 10)));
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

const STATUS_EN_PRODUCCION = 'en_produccion';

const GuiasProduccionAnalisis = () => {
  const { tipoSlug, id } = useParams();
  const config = getGuiaConfigBySlug(tipoSlug);
  const basePath = `/produccion/guias/${tipoSlug}`;
  const canMutate = useCanMutate();
  const { produccionFabricarPepDesdePtEnabled, guidesRemoveStockRestrictions } = useModuleSettings();

  const [guia, setGuia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recetasFullById, setRecetasFullById] = useState(() => new Map());
  const [recetasCargando, setRecetasCargando] = useState(false);
  const [stockCostByProduct, setStockCostByProduct] = useState({});
  const [productUnitById, setProductUnitById] = useState({});
  /** product_id → { linked_to_recipe, output_product_type } para tipar insumos vs subproductos */
  const [productTipoById, setProductTipoById] = useState({});
  /** product_id → id de receta PEP */
  const [pepRecetaIdByProductId, setPepRecetaIdByProductId] = useState({});
  /** product_id → ficha completa de receta PEP */
  const [pepRecetasByProductId, setPepRecetasByProductId] = useState(() => new Map());
  const [pepRecetasCargando, setPepRecetasCargando] = useState(false);
  const [loadingKardex, setLoadingKardex] = useState(false);
  const [insumoDetalleModal, setInsumoDetalleModal] = useState(null);
  const [expandedRecetaId, setExpandedRecetaId] = useState(null);
  const [expandedPepProductId, setExpandedPepProductId] = useState(null);
  const [confirmSacarOpen, setConfirmSacarOpen] = useState(false);
  const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);
  const [tratarComoVenta, setTratarComoVenta] = useState(false);
  const [sacando, setSacando] = useState(false);
  const [revirtiendo, setRevirtiendo] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const esProductoTerminado = config?.guideType === 'producto_terminado';

  const loadGuia = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const res = await produccionGuiaService.get(id);
      if (!res.success || !res.data) {
        setError(res.message || 'Guía no encontrada');
        setGuia(null);
        return;
      }
      setGuia(res.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Error al cargar la guía');
      setGuia(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadGuia();
  }, [loadGuia]);

  const rows = guia?.rows || [];
  const itemsWithQty = useMemo(() => getGuiaItemsWithQuantity(rows), [rows]);

  useEffect(() => {
    let cancel = false;
    if (!itemsWithQty.length) {
      setRecetasFullById(new Map());
      setRecetasCargando(false);
      return undefined;
    }
    setRecetasCargando(true);
    (async () => {
      const m = new Map();
      await Promise.all(
        itemsWithQty.map(async (item) => {
          const idStr = String(item.receta_plato_id);
          if (m.has(idStr)) return;
          try {
            const res = await recetaPlatoService.get(idStr);
            if (res?.success && res.data) m.set(idStr, res.data);
          } catch {
            /* sin ficha: costo 0 */
          }
        }),
      );
      if (!cancel) {
        setRecetasFullById(m);
        setRecetasCargando(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [itemsWithQty]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [productsRes, recetasRes] = await Promise.all([
          productService.getProducts({ active: true }),
          recetaPlatoService.list({ active_only: true }).catch(() => null),
        ]);
        if (cancel) return;
        const productsRaw = Array.isArray(productsRes?.data)
          ? productsRes.data
          : Array.isArray(productsRes?.data?.data)
            ? productsRes.data.data
            : [];
        const unitsMap = {};
        const tipoMap = {};
        const pepIdMap = {};
        productsRaw.forEach((p) => {
          if (p?.id == null) return;
          const id = String(p.id);
          const u = String(p?.unit_of_measure ?? '').trim();
          if (u) unitsMap[id] = u;
          tipoMap[id] = {
            linked_to_recipe: Boolean(p.linked_to_recipe),
            output_product_type: null,
            is_insumo: Boolean(p.is_insumo),
          };
        });

        const recetasRaw = Array.isArray(recetasRes?.data)
          ? recetasRes.data
          : Array.isArray(recetasRes?.data?.data)
            ? recetasRes.data.data
            : [];
        recetasRaw.forEach((r) => {
          const register = (productId, outputType, recetaId) => {
            if (productId == null) return;
            const id = String(productId);
            const prev = tipoMap[id] || { linked_to_recipe: true, output_product_type: null, is_insumo: false };
            tipoMap[id] = {
              ...prev,
              linked_to_recipe: true,
              output_product_type: outputType || prev.output_product_type || null,
            };
            if (outputType === 'producto_en_proceso' && recetaId != null) {
              pepIdMap[id] = recetaId;
            }
          };

          register(r?.product_id, r.output_product_type, r.id);
          (r.outputs || []).forEach((out) => {
            register(out.product_id ?? out.finished_product?.id, out.output_product_type, r.id);
          });
        });

        setProductUnitById(unitsMap);
        setProductTipoById(tipoMap);
        setPepRecetaIdByProductId(pepIdMap);
      } catch {
        if (!cancel) {
          setProductUnitById({});
          setProductTipoById({});
          setPepRecetaIdByProductId({});
        }
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const whId = guia?.warehouse_id != null ? String(guia.warehouse_id) : '';
  const ymd = guia?.guide_date || '';
  const horaCorte = normalizarHoraHHmm(guia?.guide_time, '08:00');

  useEffect(() => {
    if (!whId || !ymd) {
      setStockCostByProduct({});
      return undefined;
    }
    let cancel = false;
    setLoadingKardex(true);
    entryGuideService
      .getProductStockAndCostBulk(whId, null, ymd, horaCorte, true)
      .then((resp) => {
        if (cancel) return;
        if (resp?.success && resp?.data && typeof resp.data === 'object') {
          setStockCostByProduct(resp.data);
        } else {
          setStockCostByProduct({});
        }
      })
      .catch(() => {
        if (!cancel) setStockCostByProduct({});
      })
      .finally(() => {
        if (!cancel) setLoadingKardex(false);
      });
    return () => {
      cancel = true;
    };
  }, [whId, ymd, horaCorte]);

  const resumenInsumos = useMemo(
    () => buildResumenInsumosGuia(itemsWithQty, recetasFullById),
    [itemsWithQty, recetasFullById],
  );

  const subproductoIdsEnGuia = useMemo(() => {
    if (!esProductoTerminado) return [];
    const ids = new Set();
    for (const row of resumenInsumos.filas || []) {
      if (row?.product_id == null) continue;
      const pid = String(row.product_id);
      if (isSubproductoProduct(row.product_id, productTipoById) && pepRecetaIdByProductId[pid]) {
        ids.add(pid);
      }
    }
    return Array.from(ids).sort();
  }, [esProductoTerminado, resumenInsumos, productTipoById, pepRecetaIdByProductId]);

  useEffect(() => {
    let cancel = false;
    if (!esProductoTerminado || subproductoIdsEnGuia.length === 0) {
      setPepRecetasByProductId(new Map());
      setPepRecetasCargando(false);
      return undefined;
    }
    setPepRecetasCargando(true);
    (async () => {
      const m = new Map();
      await Promise.all(
        subproductoIdsEnGuia.map(async (pid) => {
          const recetaId = pepRecetaIdByProductId[pid];
          if (recetaId == null) return;
          try {
            const res = await recetaPlatoService.get(recetaId);
            if (res?.success && res.data) m.set(pid, res.data);
          } catch {
            /* sin ficha PEP */
          }
        }),
      );
      if (!cancel) {
        setPepRecetasByProductId(m);
        setPepRecetasCargando(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [esProductoTerminado, subproductoIdsEnGuia, pepRecetaIdByProductId]);

  const explosionPep = useMemo(() => {
    if (!esProductoTerminado) {
      return {
        hasPep: false,
        insumosPep: { filas: [], totalCosto: 0 },
        insumosAgregados: { filas: [], totalCosto: 0 },
        resumenSubproductos: { rows: [], costTotal: 0 },
        pepUnitCostByProductId: {},
        desglosePep: new Map(),
        desgloseAgregados: new Map(),
      };
    }
    return buildExplosionPepAnalisis({
      itemsWithQty,
      recetasFullById,
      pepRecetasByProductId,
      stockCostByProduct,
      productUnitById,
      productTipoById,
      globalFabricarSubrecetaDefault: produccionFabricarPepDesdePtEnabled,
    });
  }, [
    esProductoTerminado,
    itemsWithQty,
    recetasFullById,
    pepRecetasByProductId,
    stockCostByProduct,
    productUnitById,
    productTipoById,
    produccionFabricarPepDesdePtEnabled,
  ]);

  const fabricarPepDesdePt = explosionPep.hasPep;

  const resumenInsumosKardexBase = useMemo(
    () => applyKardexToInsumoResumen(resumenInsumos, stockCostByProduct, productUnitById),
    [resumenInsumos, stockCostByProduct, productUnitById],
  );

  const resumenInsumosKardex = explosionPep.hasPep
    ? explosionPep.insumosAgregados
    : resumenInsumosKardexBase;

  const resumenInsumosPepKardex = explosionPep.hasPep
    ? explosionPep.insumosPep
    : { filas: [], totalCosto: 0 };

  /** product_id → cantidad que saldrá primero en fabricación PEP (misma unidad canónica). */
  const consumoPepByProductId = useMemo(() => {
    const map = new Map();
    for (const row of resumenInsumosPepKardex.filas || []) {
      if (row?.product_id == null) continue;
      const pid = String(row.product_id);
      map.set(pid, (map.get(pid) || 0) + Number(row.quantity || 0));
    }
    return map;
  }, [resumenInsumosPepKardex]);

  /** Insumos PT: stock disponible = kárdex − consumo PEP si el producto se repite. */
  const resumenInsumosKardexPt = useMemo(() => {
    const filas = (resumenInsumosKardex.filas || []).map((row) => {
      if (row.esSubproductoCosteado || row.product_id == null) return row;
      const consumoPep = Number(consumoPepByProductId.get(String(row.product_id)) || 0);
      if (!(consumoPep > 0)) return row;
      const stockAntes = row.stockKardex == null ? null : Number(row.stockKardex);
      const stockDisponible =
        stockAntes == null || !Number.isFinite(stockAntes) ? null : stockAntes - consumoPep;
      return {
        ...row,
        stockAntesPep: stockAntes,
        consumoPep,
        stockKardex: stockDisponible,
        stockAjustadoPorPep: true,
      };
    });
    return { ...resumenInsumosKardex, filas };
  }, [resumenInsumosKardex, consumoPepByProductId]);

  const resumenPlatos = useMemo(
    () =>
      buildResumenPlatosGuia(
        itemsWithQty,
        recetasFullById,
        stockCostByProduct,
        explosionPep.hasPep ? explosionPep.pepUnitCostByProductId : {},
      ),
    [itemsWithQty, recetasFullById, stockCostByProduct, explosionPep],
  );

  /** Subproductos recuperados de insumos (ej. pepas = % del bruto de papaya). */
  const resumenByproducts = useMemo(
    () => buildResumenByproductsFromInsumos(itemsWithQty, recetasFullById, productUnitById),
    [itemsWithQty, recetasFullById, productUnitById],
  );

  const desgloseInsumoPorReceta = useMemo(() => {
    if (explosionPep.hasPep) return explosionPep.desgloseAgregados;
    return buildDesgloseInsumoPorReceta(itemsWithQty, recetasFullById, stockCostByProduct, productUnitById);
  }, [explosionPep, itemsWithQty, recetasFullById, stockCostByProduct, productUnitById]);

  const desgloseInsumoPepPorReceta = explosionPep.hasPep ? explosionPep.desglosePep : new Map();

  const toggleRecetaExpand = (recetaId) => {
    setExpandedRecetaId((prev) => (prev === recetaId ? null : recetaId));
  };

  const togglePepExpand = (productId) => {
    setExpandedPepProductId((prev) => (prev === productId ? null : productId));
  };

  const enProduccion = guia?.production_status === STATUS_EN_PRODUCCION;

  const platosParaInventario = useMemo(
    () => (resumenPlatos?.rows || []).filter((r) => r.product_id != null && Number(r.quantity) > 0),
    [resumenPlatos],
  );

  const recetasSinProducto = useMemo(
    () => (resumenPlatos?.rows || []).filter((r) => !r.product_id && Number(r.quantity) > 0),
    [resumenPlatos],
  );

  const insumosSinStock = useMemo(() => {
    const rows = [];
    const seen = new Set();
    const pushIfShort = (row, origen) => {
      if (!row || row.esSubproductoCosteado) return;
      if (!insumoSinStockSuficiente(row)) return;
      const key = `${row.product_id ?? row.product}|||${row.unit}`;
      if (seen.has(key)) return;
      seen.add(key);
      const stockMostrado = row.stockKardex == null ? 0 : Number(row.stockKardex);
      rows.push({
        product: row.product,
        unit: row.unit,
        quantity: Number(row.quantity || 0),
        stock: stockMostrado,
        stockAntesPep: row.stockAntesPep,
        consumoPep: row.consumoPep,
        origen,
      });
    };
    if (explosionPep.hasPep) {
      (resumenInsumosPepKardex.filas || []).forEach((r) => pushIfShort(r, 'PEP'));
      (resumenInsumosKardexPt.filas || []).forEach((r) => pushIfShort(r, 'PT'));
    } else {
      (resumenInsumosKardexPt.filas || []).forEach((r) => pushIfShort(r, 'PT'));
    }
    rows.sort((a, b) => String(a.product).localeCompare(String(b.product), 'es'));
    return rows;
  }, [explosionPep.hasPep, resumenInsumosPepKardex, resumenInsumosKardexPt]);

  const bloqueadoPorStock = insumosSinStock.length > 0 && !guidesRemoveStockRestrictions;

  const puedeSacarAProduccion =
    canMutate &&
    !enProduccion &&
    itemsWithQty.length > 0 &&
    resumenInsumosKardexPt.filas.length > 0 &&
    platosParaInventario.length > 0 &&
    !recetasCargando &&
    !pepRecetasCargando &&
    !loadingKardex &&
    !bloqueadoPorStock;

  const handleSacarAProduccion = async () => {
    if (!id || !guia?.warehouse_id) return;
    const filasInsumosPrecio = explosionPep.hasPep
      ? [
          ...resumenInsumosPepKardex.filas,
          ...resumenInsumosKardexPt.filas.filter((r) => !r.esSubproductoCosteado),
        ]
      : resumenInsumosKardexPt.filas;
    const insumos = filasInsumosPrecio
      .filter((r) => r?.product_id != null && Number(r.quantity || 0) > 0)
      .map((r) => ({
        product_id: Number(r.product_id),
        description: r.product || null,
        project_id: guia.project_id ? Number(guia.project_id) : null,
        quantity: Number(r.quantity || 0),
        unit_of_measure: r.unit || null,
        unit_price: Number(r.costoUnitarioKardex || 0),
        condition: 'good',
      }));
    const platos = platosParaInventario.map((r) => {
      const pid = r.product_id != null ? String(r.product_id) : '';
      const canonicalUnit =
        (pid && productUnitById[pid]) ||
        (r.unit_of_measure && String(r.unit_of_measure).toLowerCase() === 'unidad' ? 'UN' : r.unit_of_measure) ||
        (r.output_unit_of_measure && String(r.output_unit_of_measure).toLowerCase() === 'unidad'
          ? 'UN'
          : r.output_unit_of_measure) ||
        'UN';
      return {
        product_id: Number(r.product_id),
        description: r.receta_name || null,
        project_id: guia.project_id ? Number(guia.project_id) : null,
        quantity: Number(r.quantity || 0),
        unit_of_measure: canonicalUnit,
        unit_price: Number(r.unitPrice || 0),
        condition: 'good',
      };
    });

    // Entrada adicional: subproductos recuperados de insumos (costo 0).
    // Líneas separadas (no fusionar con elaborados) para no distorsionar el costo unitario.
    for (const bp of resumenByproducts.rows || []) {
      if (!(Number(bp.quantity) > 0) || bp.product_id == null) continue;
      const pid = String(bp.product_id);
      platos.push({
        product_id: Number(bp.product_id),
        description: bp.product || 'Subproducto recuperado',
        project_id: guia.project_id ? Number(guia.project_id) : null,
        quantity: Number(bp.quantity || 0),
        unit_of_measure: bp.unit || productUnitById[pid] || 'UN',
        unit_price: 0,
        condition: 'good',
      });
    }

    setSacando(true);
    try {
      const res = await produccionGuiaService.sacarAProduccion(id, {
        insumos,
        platos,
        tratar_como_venta: esProductoTerminado && tratarComoVenta,
      });
      if (res.success) {
        setConfirmSacarOpen(false);
        setTratarComoVenta(false);
        let msg = `Sacado a producción. Salida insumos: ${res.data?.salida_guide_code || '—'}. Entrada elaborados: ${res.data?.entrada_guide_code || '—'}.`;
        if (res.data?.fabricar_pep) {
          msg = `Sacado a producción con PEP. PEP salida/entrada: ${res.data?.pep_salida_guide_code || '—'} / ${res.data?.pep_entrada_guide_code || '—'}. PT salida/entrada: ${res.data?.salida_guide_code || '—'} / ${res.data?.entrada_guide_code || '—'}.`;
        }
        if (res.data?.tratado_como_venta && res.data?.venta_guide_code) {
          msg += ` Salida venta: ${res.data.venta_guide_code}.`;
        }
        setToast({ isOpen: true, message: msg, type: 'success' });
        await loadGuia();
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo sacar a producción', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || e.message || 'Error al sacar a producción',
        type: 'error',
      });
    } finally {
      setSacando(false);
    }
  };

  const handleRevertirSalida = async () => {
    if (!id) return;
    setRevirtiendo(true);
    try {
      const res = await produccionGuiaService.revertirSalidaProduccion(id);
      if (res.success) {
        setConfirmRevertOpen(false);
        setToast({ isOpen: true, message: 'Salida a producción revertida.', type: 'success' });
        await loadGuia();
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo revertir', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || e.message || 'Error al revertir',
        type: 'error',
      });
    } finally {
      setRevirtiendo(false);
    }
  };

  if (!config) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <p className="text-red-600">Tipo de guía no válido.</p>
        <Link to="/produccion/guias" className="text-teal-700 text-sm mt-2 inline-block hover:underline">
          Volver
        </Link>
      </div>
    );
  }

  if (loading) {
    return <div className="p-8 text-center text-gray-600">Cargando análisis…</div>;
  }

  if (error || !guia) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <p className="text-red-600">{error || 'Guía no encontrada'}</p>
        <Link to={basePath} className="text-teal-700 text-sm mt-2 inline-block hover:underline">
          ← Volver al listado
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/produccion" className="hover:text-teal-600">
          Producción
        </Link>
        <span className="mx-2">/</span>
        <Link to={basePath} className="hover:text-teal-600">
          {config.shortTitle}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Costos e insumos</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Costos e insumos de producción</h1>
          <p className="text-sm text-gray-600 mt-1">
            Guía <span className="font-mono font-medium text-gray-900">{guia.code}</span>
            {' · '}
            {guia.guide_date}
            {guia.guide_time ? ` ${guia.guide_time}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {canMutate && !enProduccion ? (
            <Link
              to={`${basePath}/editar/${id}`}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Editar guía
            </Link>
          ) : (
            <Link
              to={`${basePath}/editar/${id}`}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Ver guía
            </Link>
          )}
          <Link
            to={basePath}
            className="rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-800 hover:bg-teal-100"
          >
            ← Listado
          </Link>
        </div>
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm text-sm text-gray-700 space-y-1">
        <p>
          <strong className="text-gray-900">Almacén de salida:</strong>{' '}
          {guia.warehouse_name || '—'}
        </p>
        <p>
          <strong className="text-gray-900">Proyecto u operación:</strong>{' '}
          {guia.project_name || '—'}
          {guia.project_type === 'operacion' ? (
            <span className="text-gray-500 ml-1">(operación)</span>
          ) : null}
        </p>
        <p>
          <strong className="text-gray-900">Corte kárdex:</strong>{' '}
          {ymd} {horaCorte}
          {loadingKardex ? (
            <span className="text-amber-700 ml-2">(actualizando costos…)</span>
          ) : null}
        </p>
        {recetasCargando || pepRecetasCargando ? (
          <p className="text-amber-700 text-xs">Cargando fichas de recetas…</p>
        ) : null}
        {enProduccion ? (
          <p className="pt-2">
            <span className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-900">
              En producción
            </span>
            {guia.pep_salida_guide_code ? (
              <span className="ml-2 text-gray-700">
                PEP salida:{' '}
                {guia.pep_salida_guide_id ? (
                  <Link
                    to={`/exit-guides/${guia.pep_salida_guide_id}/edit`}
                    className="font-mono text-teal-800 hover:underline"
                  >
                    {guia.pep_salida_guide_code}
                  </Link>
                ) : (
                  <span className="font-mono">{guia.pep_salida_guide_code}</span>
                )}
              </span>
            ) : null}
            {guia.pep_entrada_guide_code ? (
              <span className="ml-2 text-gray-700">
                PEP entrada:{' '}
                {guia.pep_entrada_guide_id ? (
                  <Link
                    to={`/entry-guides/${guia.pep_entrada_guide_id}/edit`}
                    className="font-mono text-teal-800 hover:underline"
                  >
                    {guia.pep_entrada_guide_code}
                  </Link>
                ) : (
                  <span className="font-mono">{guia.pep_entrada_guide_code}</span>
                )}
              </span>
            ) : null}
            {guia.salida_guide_code ? (
              <span className="ml-2 text-gray-700">
                Salida:{' '}
                {guia.salida_guide_id ? (
                  <Link
                    to={`/exit-guides/${guia.salida_guide_id}/edit`}
                    className="font-mono text-teal-800 hover:underline"
                  >
                    {guia.salida_guide_code}
                  </Link>
                ) : (
                  <span className="font-mono">{guia.salida_guide_code}</span>
                )}
              </span>
            ) : null}
            {guia.entrada_guide_code ? (
              <span className="ml-2 text-gray-700">
                Entrada:{' '}
                {guia.entrada_guide_id ? (
                  <Link
                    to={`/entry-guides/${guia.entrada_guide_id}/edit`}
                    className="font-mono text-teal-800 hover:underline"
                  >
                    {guia.entrada_guide_code}
                  </Link>
                ) : (
                  <span className="font-mono">{guia.entrada_guide_code}</span>
                )}
              </span>
            ) : null}
            {guia.tratado_como_venta && guia.venta_guide_code ? (
              <span className="ml-2 text-gray-700">
                Venta:{' '}
                {guia.venta_guide_id ? (
                  <Link
                    to={`/exit-guides/${guia.venta_guide_id}/edit`}
                    className="font-mono text-teal-800 hover:underline"
                  >
                    {guia.venta_guide_code}
                  </Link>
                ) : (
                  <span className="font-mono">{guia.venta_guide_code}</span>
                )}
              </span>
            ) : null}
            <span className="block text-xs text-gray-500 mt-1">
              Insumos y elaborados se movieron en el mismo almacén ({guia.warehouse_name || '—'}).
            </span>
          </p>
        ) : null}
      </div>

      {itemsWithQty.length > 0 && canMutate ? (
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-gray-700">
            {enProduccion ? (
              <span>
                La salida a producción ya fue confirmada. Puede revertirla para eliminar las guías de inventario
                generadas.
              </span>
            ) : (
              <span>
                Confirme para generar la <strong>guía de salida de insumos</strong> y la{' '}
                <strong>guía de entrada de elaborados</strong> en el mismo almacén.
                {esProductoTerminado ? (
                  <> Opcionalmente puede registrar además una <strong>salida por venta</strong>.</>
                ) : null}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {enProduccion ? (
              <button
                type="button"
                onClick={() => setConfirmRevertOpen(true)}
                disabled={revirtiendo}
                className="rounded-md border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-900 hover:bg-rose-100 disabled:opacity-50"
              >
                {revirtiendo ? 'Revirtiendo…' : 'Revertir salida a producción'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmSacarOpen(true)}
                disabled={!puedeSacarAProduccion || sacando}
                title={
                  bloqueadoPorStock
                    ? 'Hay insumos sin stock suficiente'
                    : undefined
                }
                className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sacando ? 'Procesando…' : 'Sacar a producción'}
              </button>
            )}
          </div>
        </div>
      ) : null}

      {recetasSinProducto.length > 0 && !enProduccion ? (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {recetasSinProducto.length} receta(s) sin producto vinculado en catálogo; no se incluirán en la guía de
          entrada hasta configurar el producto en la ficha de receta:{' '}
          {recetasSinProducto.map((r) => r.receta_name).join(', ')}
        </div>
      ) : null}

      {insumosSinStock.length > 0 && !enProduccion ? (
        <div className="mb-4 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          <p className="font-semibold">
            {bloqueadoPorStock
              ? 'No se puede sacar a producción: stock insuficiente'
              : 'Advertencia: stock insuficiente (puede forzar si la restricción está desactivada)'}
          </p>
          <ul className="mt-2 list-disc pl-5 space-y-0.5">
            {insumosSinStock.map((r) => (
              <li key={`${r.product}-${r.unit}-${r.origen}`}>
                <strong>{r.product}</strong> ({r.unit}): necesita{' '}
                {formatearCantidadReceta4Dec(r.quantity)}, stock{' '}
                {Number(r.stock).toLocaleString('es-PE', { maximumFractionDigits: 3 })}
                {r.origen === 'PEP' ? ' · insumo de subproducto' : ''}
                {r.origen === 'PT' && r.consumoPep > 0
                  ? ` · tras PEP (${Number(r.stockAntesPep || 0).toLocaleString('es-PE', { maximumFractionDigits: 3 })} − ${Number(r.consumoPep).toLocaleString('es-PE', { maximumFractionDigits: 4 })})`
                  : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {itemsWithQty.length === 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-900">
          Esta guía no tiene cantidades elaboradas. Indique al menos una cantidad en la guía para ver el análisis.
          <Link to={`${basePath}/editar/${id}`} className="block mt-2 font-medium text-teal-800 hover:underline">
            Ir a editar guía
          </Link>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <div className="rounded-lg border border-teal-200 bg-teal-50/60 p-4 max-w-sm">
              <p className="text-xs font-medium text-teal-800 uppercase tracking-wide">Costo total insumos</p>
              <p className="text-xl font-bold text-teal-900 mt-1">{fmtMoney(resumenInsumosKardex.totalCosto)}</p>
            </div>
          </div>

          <section className="mb-8">
            <h2 className="text-sm font-semibold text-gray-800 mb-2">Lo que se está produciendo</h2>
            <p className="text-xs text-gray-500 mb-3">
              Por cada receta: cantidad elaborada y costo promedio por unidad de salida (insumos × kárdex).
            </p>
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <th className="px-3 py-2">Receta</th>
                    <th className="px-3 py-2">Clasificación</th>
                    <th className="px-3 py-2 text-right">Cant. elaborada</th>
                    <th className="px-3 py-2 text-right">Costo total</th>
                    <th className="px-3 py-2 text-right">Costo unitario</th>
                    <th className="px-3 py-2 text-center w-24">Insumos</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {resumenPlatos.rows.map((row) => {
                    const isOpen = expandedRecetaId === row.receta_plato_id;
                    const receta = recetasFullById.get(String(row.receta_plato_id));
                    return (
                      <React.Fragment key={row.receta_plato_id}>
                        <tr className="hover:bg-gray-50/80">
                          <td className="px-3 py-2">
                            <span className="font-medium text-gray-900">{row.receta_name}</span>
                            {row.receta_code ? (
                              <span className="block text-xs text-gray-500 font-mono">{row.receta_code}</span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-gray-600 text-xs max-w-[200px]">{row.classification}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatearCantidadReceta4Dec(row.quantity)}{' '}
                            <span className="text-gray-500 text-xs">{row.output_unit_of_measure}</span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(row.totalCost)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-teal-800">{formatearCostoUnitario4Dec(row.unitPrice)}</td>
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => toggleRecetaExpand(row.receta_plato_id)}
                              disabled={!receta}
                              className="text-xs font-medium text-teal-700 hover:underline disabled:text-gray-400"
                            >
                              {isOpen ? 'Ocultar' : 'Ver'}
                            </button>
                          </td>
                        </tr>
                        {isOpen && receta ? (
                          <tr className="bg-gray-50/90">
                            <td colSpan={6} className="px-4 py-3">
                              <DetalleRecetaInsumos
                                receta={receta}
                                qty={row.quantity}
                                stockCostByProduct={stockCostByProduct}
                                productUnitById={productUnitById}
                                productTipoById={productTipoById}
                                ingredientCostOverrides={
                                  explosionPep.hasPep ? explosionPep.pepUnitCostByProductId : {}
                                }
                              />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-teal-50/50 font-semibold">
                    <td colSpan={3} className="px-3 py-2 text-right text-gray-800">
                      Totales
                    </td>
                    <td colSpan={3} className="px-3 py-2 text-right text-teal-900">
                      {fmtMoney(resumenPlatos.costTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {resumenByproducts.rows.length > 0 ? (
            <section className="mb-8">
              <h2 className="text-sm font-semibold text-amber-900 mb-2">
                Subproductos recuperados de insumos
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Al sacar a producción entran a inventario junto con los elaborados (cantidad = % del bruto del
                insumo). Costo de entrada: 0.
              </p>
              <div className="overflow-x-auto rounded-lg border border-amber-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-amber-50 text-left text-xs uppercase text-amber-800">
                      <th className="px-3 py-2">Subproducto</th>
                      <th className="px-3 py-2">Origen (insumo)</th>
                      <th className="px-3 py-2 text-right">% recup.</th>
                      <th className="px-3 py-2 text-right">Cant. a ingresar</th>
                      <th className="px-3 py-2 text-right">Costo entrada</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {resumenByproducts.rows.map((row) => (
                      <tr key={`bp-${row.product_id}-${row.unit}`} className="hover:bg-amber-50/40">
                        <td className="px-3 py-2 font-medium text-gray-900">{row.product}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{row.source_product || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                          {Number(row.byproduct_pct || 0).toLocaleString('es-PE', {
                            maximumFractionDigits: 4,
                          })}{' '}
                          %
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatearCantidadReceta4Dec(row.quantity)}{' '}
                          <span className="text-gray-500 text-xs">{row.unit}</span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtMoney(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {explosionPep.hasPep && explosionPep.resumenSubproductos.rows.length > 0 ? (
            <section className="mb-8">
              <h2 className="text-sm font-semibold text-violet-900 mb-2">Subproductos a fabricar</h2>
              <p className="text-xs text-gray-500 mb-3">
                Productos en proceso que se elaboran automáticamente al sacar a producción (costo = insumos de su
                receta × kárdex).
              </p>
              <div className="overflow-x-auto rounded-lg border border-violet-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-violet-50 text-left text-xs uppercase text-violet-700">
                      <th className="px-3 py-2">Subproducto</th>
                      <th className="px-3 py-2">Clasificación</th>
                      <th className="px-3 py-2 text-right">Cant. elaborada</th>
                      <th className="px-3 py-2 text-right">Costo total</th>
                      <th className="px-3 py-2 text-right">Costo unitario</th>
                      <th className="px-3 py-2 text-center w-24">Insumos</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {explosionPep.resumenSubproductos.rows.map((row) => {
                      const pid = row.product_id != null ? String(row.product_id) : null;
                      const isOpen = expandedPepProductId === pid;
                      const pepReceta = pid ? pepRecetasByProductId.get(pid) : null;
                      return (
                        <React.Fragment key={pid || row.product_name}>
                          <tr className="hover:bg-violet-50/40">
                            <td className="px-3 py-2">
                              <span className="font-medium text-gray-900">{row.product_name}</span>
                              {row.receta_code ? (
                                <span className="block text-xs text-gray-500 font-mono">{row.receta_code}</span>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 text-gray-600 text-xs max-w-[200px]">{row.classification}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {formatearCantidadReceta4Dec(row.quantity)}{' '}
                              <span className="text-gray-500 text-xs">{row.unit_of_measure}</span>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">
                              {fmtMoney(row.totalCost)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-violet-800">
                              {formatearCostoUnitario4Dec(row.unitPrice)}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                type="button"
                                onClick={() => togglePepExpand(pid)}
                                disabled={!pepReceta}
                                className="text-xs font-medium text-violet-700 hover:underline disabled:text-gray-400"
                              >
                                {isOpen ? 'Ocultar' : 'Ver'}
                              </button>
                            </td>
                          </tr>
                          {isOpen && pepReceta ? (
                            <tr className="bg-violet-50/50">
                              <td colSpan={6} className="px-4 py-3">
                                <DetalleRecetaInsumos
                                  receta={pepReceta}
                                  qty={row.quantity}
                                  stockCostByProduct={stockCostByProduct}
                                  productUnitById={productUnitById}
                                  productTipoById={productTipoById}
                                />
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-violet-50/80 font-semibold">
                      <td colSpan={3} className="px-3 py-2 text-right text-violet-900">
                        Totales
                      </td>
                      <td colSpan={3} className="px-3 py-2 text-right text-violet-900">
                        {fmtMoney(explosionPep.resumenSubproductos.costTotal)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
          ) : null}

          {explosionPep.hasPep ? (
            <section>
              <h2 className="text-sm font-semibold text-violet-900 mb-2">Insumos de subproductos</h2>
              <p className="text-xs text-gray-500 mb-3">
                Insumos hoja que se consumen al fabricar los subproductos (PEP) un minuto antes del producto
                terminado. Costo unitario desde kárdex al corte.
              </p>
              {resumenInsumosPepKardex.filas.length === 0 ? (
                <p className="text-sm text-gray-500">Sin insumos de subproductos.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-violet-200 bg-white shadow-sm">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-violet-50 text-left text-xs uppercase text-violet-700">
                        <th className="px-3 py-2">Producto / insumo</th>
                        <th className="px-3 py-2">Unidad</th>
                        <th className="px-3 py-2 text-right">Cantidad total</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap">C. unit</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap">Stock</th>
                        <th className="px-3 py-2 text-right">Subtotal est.</th>
                        <th className="px-3 py-2 text-center">Detalle</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {resumenInsumosPepKardex.filas.map((row, i) => {
                        const detailKey = row.product_id != null
                          ? `id:${String(row.product_id)}|||${row.unit}`
                          : `name:${row.product}|||${row.unit}`;
                        const detailRows = desgloseInsumoPepPorReceta.get(detailKey) || [];
                        const sinStock = insumoSinStockSuficiente(row);
                        return (
                          <tr
                            key={`pep-${row.product}-${row.unit}-${i}`}
                            className={
                              sinStock
                                ? 'bg-rose-50 hover:bg-rose-100/80'
                                : 'hover:bg-violet-50/40'
                            }
                          >
                            <td className="px-3 py-2 text-gray-900">
                              <span className={sinStock ? 'font-semibold text-rose-900' : ''}>
                                {row.product}
                              </span>
                              {sinStock ? (
                                <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-200 text-rose-900">
                                  Sin stock
                                </span>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 text-gray-600">{row.unit}</td>
                            <td className={`px-3 py-2 text-right tabular-nums ${sinStock ? 'text-rose-900 font-semibold' : ''}`}>
                              {formatearCantidadReceta4Dec(row.quantity)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {formatearCostoUnitario4Dec(row.costoUnitarioKardex)}
                            </td>
                            <td className={`px-3 py-2 text-right tabular-nums ${sinStock ? 'text-rose-800 font-semibold' : 'text-gray-800'}`}>
                              {row.stockKardex != null
                                ? Number(row.stockKardex).toLocaleString('es-PE', { maximumFractionDigits: 3 })
                                : '—'}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(row.subtotalKardex)}</td>
                            <td className="px-3 py-2 text-center">
                              <button
                                type="button"
                                onClick={() =>
                                  setInsumoDetalleModal({
                                    product: row.product,
                                    unit: row.unit,
                                    totalQuantity: row.quantity,
                                    totalCost: row.subtotalKardex,
                                    detailRows,
                                  })
                                }
                                disabled={detailRows.length === 0}
                                className="text-xs font-medium text-violet-700 hover:underline disabled:text-gray-400 disabled:no-underline"
                              >
                                Ver receta
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-violet-50/80 font-semibold">
                        <td colSpan={5} className="px-3 py-2 text-right text-violet-900">
                          Total insumos de subproductos
                        </td>
                        <td className="px-3 py-2 text-right text-violet-900">
                          {fmtMoney(resumenInsumosPepKardex.totalCosto)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </section>
          ) : null}

          <section className={explosionPep.hasPep ? 'mt-8' : ''}>
            <h2 className="text-sm font-semibold text-gray-800 mb-2">Insumos agregados</h2>
            <p className="text-xs text-gray-500 mb-3">
              {explosionPep.hasPep
                ? 'Insumos directos (kárdex) y subproductos costeados. Cantidad = bruto a despachar (neto + merma). Si un insumo también sale en PEP, el stock PT muestra: kárdex − consumo PEP.'
                : 'Suma de insumos de las recetas. La cantidad es el bruto a despachar (neto + merma planificada). Costo unitario desde kárdex del almacén al corte indicado.'}
            </p>
            {resumenInsumosKardexPt.filas.length === 0 ? (
              <p className="text-sm text-gray-500">Sin insumos calculados.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <th className="px-3 py-2">Producto / insumo</th>
                      <th className="px-3 py-2">Tipo</th>
                      <th className="px-3 py-2">Unidad</th>
                      <th className="px-3 py-2 text-right">Neto</th>
                      <th className="px-3 py-2 text-right">Merma</th>
                      <th className="px-3 py-2 text-right">Bruto</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">C. unit</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">
                        {explosionPep.hasPep ? 'Stock disp.' : 'Stock'}
                      </th>
                      <th className="px-3 py-2 text-right">Subtotal est.</th>
                      <th className="px-3 py-2 text-center">Detalle</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {resumenInsumosKardexPt.filas.map((row, i) => {
                      const detailKey = row.product_id != null
                        ? `id:${String(row.product_id)}|||${row.unit}`
                        : `name:${row.product}|||${row.unit}`;
                      const detailRows = desgloseInsumoPorReceta.get(detailKey) || [];
                      const tipo = tipoInsumoLabel(row.product_id, productTipoById);
                      const sinStock = !row.esSubproductoCosteado && insumoSinStockSuficiente(row);
                      return (
                        <tr
                          key={`${row.product}-${row.unit}-${i}`}
                          className={sinStock ? 'bg-rose-50 hover:bg-rose-100/80' : 'hover:bg-gray-50/80'}
                        >
                          <td className="px-3 py-2 text-gray-900">
                            <span className={sinStock ? 'font-semibold text-rose-900' : ''}>
                              {row.product}
                            </span>
                            {sinStock ? (
                              <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-200 text-rose-900">
                                Sin stock
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                tipo === 'Subproducto'
                                  ? 'bg-violet-100 text-violet-800'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {tipo}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-600">{row.unit}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                            {formatearCantidadReceta4Dec(row.quantity_net ?? row.quantity)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-amber-800">
                            {formatearCantidadReceta4Dec(row.quantity_merma || 0)}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums ${sinStock ? 'text-rose-900 font-semibold' : 'font-medium'}`}>
                            {formatearCantidadReceta4Dec(row.quantity)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatearCostoUnitario4Dec(row.costoUnitarioKardex)}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${sinStock ? 'text-rose-800 font-semibold' : 'text-gray-800'}`}>
                            {row.esSubproductoCosteado ? (
                              <span className="inline-block text-violet-900" title="Stock kárdex + cantidad a fabricar">
                                {Number(row.stockKardex ?? 0).toLocaleString('es-PE', { maximumFractionDigits: 3 })}
                                {' + '}
                                {Number(row.quantity || 0).toLocaleString('es-PE', { maximumFractionDigits: 4 })}
                                <span className="block text-[10px] font-semibold text-violet-700">
                                  ={' '}
                                  {(Number(row.stockKardex ?? 0) + Number(row.quantity || 0)).toLocaleString('es-PE', {
                                    maximumFractionDigits: 4,
                                  })}
                                </span>
                              </span>
                            ) : row.stockAjustadoPorPep ? (
                              <span
                                className="inline-block text-amber-900"
                                title="Stock kárdex menos salida PEP previa (mismo insumo)"
                              >
                                {Number(row.stockAntesPep ?? 0).toLocaleString('es-PE', { maximumFractionDigits: 3 })}
                                {' − '}
                                {Number(row.consumoPep || 0).toLocaleString('es-PE', { maximumFractionDigits: 4 })}
                                <span className="block text-[10px] font-semibold text-amber-800">
                                  ={' '}
                                  {Number(row.stockKardex ?? 0).toLocaleString('es-PE', { maximumFractionDigits: 4 })}
                                  <span className="font-normal text-gray-500"> disp. PT</span>
                                </span>
                              </span>
                            ) : row.stockKardex != null ? (
                              Number(row.stockKardex).toLocaleString('es-PE', { maximumFractionDigits: 3 })
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(row.subtotalKardex)}</td>
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() =>
                                setInsumoDetalleModal({
                                  product: row.product,
                                  unit: row.unit,
                                  totalQuantity: row.quantity,
                                  totalCost: row.subtotalKardex,
                                  detailRows,
                                })
                              }
                              disabled={detailRows.length === 0}
                              className="text-xs font-medium text-teal-700 hover:underline disabled:text-gray-400 disabled:no-underline"
                            >
                              Ver receta
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-teal-50/50 font-semibold">
                      <td colSpan={8} className="px-3 py-2 text-right text-gray-800">
                        Total de costos {explosionPep.hasPep ? '(insumos + subproductos)' : '(kárdex al corte)'}
                      </td>
                      <td className="px-3 py-2 text-right text-teal-900">{fmtMoney(resumenInsumosKardexPt.totalCosto)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {confirmSacarOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-sacar-produccion-titulo"
          onClick={(e) => {
            if (e.target === e.currentTarget && !sacando) setConfirmSacarOpen(false);
          }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 id="modal-sacar-produccion-titulo" className="text-lg font-semibold text-gray-900">
                Sacar a producción
              </h3>
            </div>
            <div className="px-5 py-4 space-y-4 text-sm text-gray-700">
              <p>
                Se generarán en <strong>{guia.warehouse_name || 'el almacén de la guía'}</strong> (corte kárdex{' '}
                {ymd} {horaCorte}):
              </p>
              <ul className="list-disc pl-5 space-y-1 text-gray-600">
                {explosionPep.hasPep ? (
                  <>
                    <li>
                      Fabricación de <strong>subproductos (PEP)</strong>: salida de sus insumos y entrada del
                      subproducto (hora de la guía)
                    </li>
                    <li>
                      Luego (+1 min): guía de <strong>salida</strong> de insumos/subproductos y{' '}
                      <strong>entrada</strong> de elaborados
                    </li>
                  </>
                ) : (
                  <>
                    <li>Guía de <strong>salida</strong> de insumos (evento salida a producción)</li>
                    <li>Guía de <strong>entrada</strong> de productos elaborados (evento entrada a producción)</li>
                  </>
                )}
                {resumenByproducts.rows.length > 0 ? (
                  <li>
                    También entrarán <strong>subproductos recuperados</strong> de insumos (
                    {resumenByproducts.rows.length} producto
                    {resumenByproducts.rows.length === 1 ? '' : 's'}, costo 0)
                  </li>
                ) : null}
              </ul>
              {esProductoTerminado ? (
                <label className="flex items-start gap-3 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tratarComoVenta}
                    onChange={(e) => setTratarComoVenta(e.target.checked)}
                    disabled={sacando}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>
                    <span className="font-medium text-gray-900">Tratar elaborados como venta</span>
                    <span className="block text-xs text-gray-600 mt-1">
                      Además se creará una guía de <strong>salida por venta</strong> (evento venta, entidad Ventas) con
                      los mismos productos terminados y cantidades.
                    </span>
                  </span>
                </label>
              ) : null}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <button
                type="button"
                disabled={sacando}
                onClick={() => setConfirmSacarOpen(false)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={sacando}
                onClick={handleSacarAProduccion}
                className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
              >
                {sacando ? 'Procesando…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmRevertOpen}
        onClose={() => !revirtiendo && setConfirmRevertOpen(false)}
        onConfirm={handleRevertirSalida}
        title="Revertir salida a producción"
        message="Se eliminarán las guías de salida, entrada y venta (si aplica) vinculadas. ¿Desea continuar?"
        confirmText={revirtiendo ? 'Revirtiendo…' : 'Revertir'}
        cancelText="Cancelar"
        type="danger"
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />

      {insumoDetalleModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setInsumoDetalleModal(null);
          }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 bg-teal-50/80">
              <h3 className="text-base font-semibold text-gray-900">
                Desglose por receta — {insumoDetalleModal.product}
              </h3>
              <p className="text-xs text-gray-600 mt-1">
                Unidad: {insumoDetalleModal.unit} · Cantidad total:{' '}
                {formatearCantidadReceta4Dec(insumoDetalleModal.totalQuantity)} · Costo total:{' '}
                {fmtMoney(insumoDetalleModal.totalCost)}
              </p>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1">
              {insumoDetalleModal.detailRows.length === 0 ? (
                <p className="text-sm text-gray-500">Sin detalle.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <th className="px-3 py-2">Receta</th>
                      <th className="px-3 py-2">Clasificación</th>
                      <th className="px-3 py-2 text-right">Cantidad</th>
                      <th className="px-3 py-2 text-right">C.unit</th>
                      <th className="px-3 py-2 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {insumoDetalleModal.detailRows.map((d, idx) => (
                      <tr key={`${d.receta}-${idx}`}>
                        <td className="px-3 py-2 text-gray-900">{d.receta}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{d.classification}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatearCantidadReceta4Dec(d.quantity)} {d.unit}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatearCostoUnitario4Dec(d.cUnit)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(d.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                type="button"
                onClick={() => setInsumoDetalleModal(null)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** Clasifica un ítem de receta: Subproducto (tiene receta propia) vs Producto (insumo simple). */
function tipoInsumoLabel(productId, productTipoById) {
  if (productId == null) return 'Producto';
  const meta = productTipoById?.[String(productId)];
  if (!meta) return 'Producto';
  if (meta.output_product_type === 'producto_en_proceso' || meta.output_product_type === 'producto_terminado') {
    return 'Subproducto';
  }
  if (meta.linked_to_recipe) return 'Subproducto';
  return 'Producto';
}

/** Cantidad requerida mayor que stock disponible (o sin stock conocido). */
function insumoSinStockSuficiente(row) {
  if (!row || row.esSubproductoCosteado) return false;
  const need = Number(row.quantity || 0);
  if (!(need > 0)) return false;
  if (row.stockKardex == null || !Number.isFinite(Number(row.stockKardex))) {
    return true;
  }
  return Number(row.stockKardex) + 1e-9 < need;
}

function DetalleRecetaInsumos({
  receta,
  qty,
  stockCostByProduct,
  productUnitById,
  productTipoById,
  ingredientCostOverrides = {},
}) {
  const ings = lineasInsumoEscaladosRecetaApi(receta, qty);
  const rows = ings.map((row) => {
    const pid = row.product_id != null ? String(row.product_id) : null;
    const override =
      pid != null && ingredientCostOverrides[pid] != null ? Number(ingredientCostOverrides[pid]) : null;
    const cached = pid ? (stockCostByProduct?.[pid] || stockCostByProduct?.[row.product_id]) : null;
    const canonicalUnit = pid ? (productUnitById?.[pid] || row.unit || '') : (row.unit || '');
    const cUnit =
      override != null && Number.isFinite(override)
        ? override
        : Number(cached?.average_cost ?? row.precioUnitario ?? 0);
    const safe = Number.isFinite(cUnit) ? cUnit : 0;
    const subtotal = Math.round(Number(row.quantity || 0) * safe * 100) / 100;
    const tipo = tipoInsumoLabel(row.product_id, productTipoById);
    return { ...row, unit: canonicalUnit || row.unit, cUnit: safe, subtotal, tipo };
  });
  const total = Math.round(rows.reduce((a, r) => a + r.subtotal, 0) * 100) / 100;

  return (
    <div className="text-sm">
      <p className="text-xs text-gray-500 mb-2">
        Insumos para <strong>{formatearCantidadReceta4Dec(qty)}</strong> unidad(es) elaborada(s) · Costo total:{' '}
        <strong>{fmtMoney(total)}</strong>
      </p>
      <table className="min-w-full text-xs border border-gray-200 rounded-md overflow-hidden">
        <thead>
          <tr className="bg-gray-100 text-gray-600">
            <th className="px-2 py-1 text-left">Insumo</th>
            <th className="px-2 py-1 text-left">Tipo</th>
            <th className="px-2 py-1 text-left">Unidad</th>
            <th className="px-2 py-1 text-right">Cantidad</th>
            <th className="px-2 py-1 text-right">C.unit</th>
            <th className="px-2 py-1 text-right">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <React.Fragment key={i}>
              <tr className="border-t border-gray-100">
                <td className="px-2 py-1">{r.product}</td>
                <td className="px-2 py-1">
                  <span
                    className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      r.tipo === 'Subproducto'
                        ? 'bg-violet-100 text-violet-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {r.tipo}
                  </span>
                </td>
                <td className="px-2 py-1 text-gray-600">{r.unit}</td>
                <td className="px-2 py-1 text-right tabular-nums">{formatearCantidadReceta4Dec(r.quantity)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{formatearCostoUnitario4Dec(r.cUnit)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmtMoney(r.subtotal)}</td>
              </tr>
              {r.byproduct_product_id && Number(r.byproduct_quantity) > 0 ? (
                <tr className="bg-amber-50/70 border-t border-amber-100">
                  <td colSpan={6} className="px-2 py-1 text-[11px] text-amber-900">
                    Recupera{' '}
                    <strong>
                      {formatearCantidadReceta4Dec(r.byproduct_quantity)} {r.byproduct_unit || r.unit}
                    </strong>{' '}
                    de <strong>{r.byproduct_name || `Producto #${r.byproduct_product_id}`}</strong> (
                    {Number(r.byproduct_pct || 0).toLocaleString('es-PE', { maximumFractionDigits: 4 })} % del
                    bruto)
                  </td>
                </tr>
              ) : null}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default GuiasProduccionAnalisis;
